import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarDays, MapPin, Users, Loader2, CheckCircle2, Star, Lock } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { PageHeader, Loading, Empty, DemoBadge } from "@/components/common";
import { EventCover } from "@/components/community";

export const fwhen = (s, e) => {
  const a = new Date(s);
  const day = a.toLocaleDateString("es-MX", { weekday: "short", day: "numeric", month: "short" });
  const h = (d) => d.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });
  return `${day} · ${h(a)}${e ? `–${h(new Date(e))}` : ""}`;
};

export function RegisterButton({ ev, onDone, className = "" }) {
  const [busy, setBusy] = useState(false);
  const full = ev.capacity && ev.attendee_count >= ev.capacity && !ev.registered;
  const go = async (e) => {
    e?.stopPropagation();
    setBusy(true);
    try { const { data } = await api.post(`/events/${ev.event_id}/register`); onDone?.(data); toast.success(data.registered ? "Registro confirmado · te avisamos por WhatsApp" : "Cancelaste tu registro"); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  if (ev.state === "finalizado") return <Button variant="outline" disabled className={className}>Evento finalizado</Button>;
  return (
    <Button onClick={go} disabled={busy || full} variant={ev.registered ? "secondary" : "default"} className={`btn-press ${className}`} data-testid={`event-register-${ev.event_id}`}>
      {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : ev.registered ? <CheckCircle2 className="h-4 w-4 mr-1" /> : null}
      {full ? "Cupo lleno" : ev.registered ? "Registrado · cancelar" : "Registrarme"}
    </Button>
  );
}

export default function Events() {
  const list = useFetch("/events", { poll: 20000 });
  const [tab, setTab] = useState("proximos");
  const [open, setOpen] = useState(null);
  const update = (ev) => list.setData((d) => d.map((x) => (x.event_id === ev.event_id ? ev : x)));
  const items = (list.data || []).filter((e) => (tab === "proximos" ? e.state !== "finalizado" : e.state === "finalizado"));
  const cur = open ? (list.data || []).find((e) => e.event_id === open) : null;

  return (
    <div>
      <PageHeader eyebrow="Agenda pública" title="Eventos del municipio" subtitle="Ferias, cabildos abiertos, jornadas y actividades culturales. Regístrate para asegurar tu lugar." />
      <Tabs value={tab} onValueChange={setTab} className="mb-4"><TabsList>
        <TabsTrigger value="proximos" data-testid="events-tab-proximos">Próximos</TabsTrigger>
        <TabsTrigger value="pasados" data-testid="events-tab-pasados">Pasados</TabsTrigger>
      </TabsList></Tabs>
      {list.loading ? <Loading rows={3} /> : !items.length ? <Card><CardContent><Empty icon={CalendarDays} title="Sin eventos por ahora" hint="Vuelve pronto: aquí se publican las actividades del municipio." /></CardContent></Card> : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="events-list">
          {items.map((e) => (
            <Card key={e.event_id} className={`card-hover overflow-hidden cursor-pointer flex flex-col ${e.featured ? "ring-2 ring-[#007C91]" : ""}`} onClick={() => setOpen(e.event_id)} data-testid={`event-card-${e.event_id}`}>
              <div className="relative"><EventCover event={e} />
                <div className="absolute top-2 left-2 flex gap-1">
                  {e.featured && <Badge className="bg-amber-400 text-[#16202B] hover:bg-amber-400"><Star className="h-3 w-3 mr-1" />Destacado</Badge>}
                  {e.visibility === "interno" && <Badge variant="secondary"><Lock className="h-3 w-3 mr-1" />Interno · {e.dependency_label}</Badge>}
                </div>
              </div>
              <CardContent className="p-4 flex flex-col gap-2 flex-1">
                <div className="font-semibold leading-tight">{e.title} {e.is_demo && <DemoBadge />}</div>
                <div className="text-xs text-muted-foreground flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" />{fwhen(e.starts_at, e.ends_at)}</div>
                <div className="text-xs text-muted-foreground flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{e.place}</div>
                <div className="text-xs text-muted-foreground flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />{e.attendee_count}{e.capacity ? ` / ${e.capacity}` : ""} registrados</div>
                <div className="mt-auto pt-2"><RegisterButton ev={e} onDone={update} className="w-full" /></div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <Dialog open={!!cur} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="bg-white sm:max-w-lg p-0 overflow-hidden" data-testid="event-detail-dialog">
          {cur && (<>
            <EventCover event={cur} className="h-48" />
            <div className="p-5 space-y-3">
              <DialogHeader><DialogTitle>{cur.title}</DialogTitle><DialogDescription>{fwhen(cur.starts_at, cur.ends_at)} · {cur.place}</DialogDescription></DialogHeader>
              <p className="text-sm whitespace-pre-line">{cur.description}</p>
              <div className="text-xs text-muted-foreground">{cur.attendee_count}{cur.capacity ? ` de ${cur.capacity} lugares` : " registrados"}{cur.dependency_label ? ` · Organiza: ${cur.dependency_label}` : ""}</div>
              <RegisterButton ev={cur} onDone={update} className="w-full" />
            </div>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
